import React from 'react';
import CustomNavLink from 'components/sidebar/CustomNavLink';
import { ListItemIcon, ListItemText, ListItemButton, ListItemButtonProps, styled } from '@mui/material';
import { useApp } from 'hooks/app';

interface StyledListItemProps {
  nested: number;
}

const StyledLink = styled(CustomNavLink)<StyledListItemProps>(props => ({
  padding: '7px 10px',
  flex: 0,
  paddingLeft: props.nested ? props.theme.spacing(props.nested) : 10,
  minHeight: 42,
  display: 'flex',
  cursor: 'pointer',
  alignItems: 'center',
  gap: props.theme.spacing(1),
  transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
  borderRadius: props.theme.shape.borderRadius,
  color: props.theme.palette.text.secondary,
  textDecoration: 'none',
  marginBottom: 2,
  '&.active, &:hover': {
    backgroundColor: props.theme.palette.grey[100],
    color: props.theme.palette.primary.main,
    transform: 'translateX(2px)',
    '& svg': {
      color: props.theme.palette.primary.main,
    },
  },
  '&.active': {
    backgroundColor: props.theme.palette.primary.main,
    color: props.theme.palette.primary.contrastText,
    '& svg, & span': {
      color: props.theme.palette.primary.contrastText,
    },
  },
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 30,
  color: 'inherit',
  '& > svg': {
    fontSize: 20,
  },
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  flex: 0.9,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  lineClamp: 2,
  WebkitBoxOrient: 'vertical',
  '& > span': {
    color: 'inherit',
    fontSize: 13,
    fontWeight: 600,
  },
}));

interface SidebarItemProps extends ListItemButtonProps {
  to?: string;
  text: string;
  nested?: number;
  icon?: React.ReactElement;
  onClick?: () => void | null;
  isLink?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  to,
  icon,
  text,
  nested = 0,
  onClick,
  isLink = true,
  className,
  ...rest
}) => {
  const app = useApp();

  function handleClick() {
    if (app.isMobile || app.windowWidth <= 1280) {
      app.handleOpenMenu();
    }
  }

  return (
    <StyledLink {...rest} nested={nested} className={className} to={to} onClick={onClick || handleClick}>
      {icon && <StyledListItemIcon>{icon}</StyledListItemIcon>}
      <StyledListItemText primary={text} />
    </StyledLink>
  );
};

export default SidebarItem;
